import { useState, useEffect, useCallback } from 'react'
import { ChevronDown, FileText, RefreshCw } from 'lucide-react'
import leiloesService from '../../services/leiloesService'
import mapasService from '../../services/mapasService'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import FaturaCompradorPreview from './FaturaCompradorPreview'

function Toggle({ checked, onChange, label }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer select-none">
      <div
        onClick={() => onChange(!checked)}
        className={`relative w-9 h-5 rounded-full transition-colors duration-200 ${checked ? 'bg-blue-600' : 'bg-gray-300'}`}
      >
        <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${checked ? 'translate-x-4' : 'translate-x-0'}`} />
      </div>
      {label && <span className="text-sm text-gray-600">{label}</span>}
    </label>
  )
}

function Select({ label, value, onChange, children, disabled }) {
  return (
    <div className="flex flex-col gap-1 w-full">
      {label && <label className="text-xs text-gray-500 font-medium">{label}</label>}
      <div className="relative">
        <select
          value={value}
          onChange={e => onChange(e.target.value)}
          disabled={disabled}
          className="w-full appearance-none border border-gray-300 rounded-lg px-3 py-2.5 pr-8 text-sm bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
        >
          {children}
        </select>
        <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      </div>
    </div>
  )
}

export default function FaturaComprador() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id

  const [leiloes,     setLeiloes]     = useState([])
  const [compradores, setCompradores] = useState([])
  const [leilaoId,    setLeilaoId]    = useState('')
  const [compradorId, setCompradorId] = useState('')
  const [tipo,        setTipo]        = useState('padrao')
  const [dbVendedor,  setDbVendedor]  = useState(false)
  const [cfgBlocos,   setCfgBlocos]   = useState({ medias_sexo: true, media_geral: true, gta_nota: true, ocultar_linha_gta_nota: false, ocultar_coluna_gado: false, ocultar_parcelas: false })
  const [colunasAtivas, setColunasAtivas] = useState(['lote', 'vendedor', 'qtd', 'raca', 'sexo', 'peso', 'valor_unitario', 'valor_total', 'comissao_comprador', 'total_liquido', 'prazo'])
  const [loadingLeil, setLoadingLeil] = useState(false)
  const [loadingComp, setLoadingComp] = useState(false)
  const [loadingPrev, setLoadingPrev] = useState(false)
  const [dados,       setDados]       = useState(null)
  const [erro,        setErro]        = useState(null)
  const [separacao,   setSeparacao]   = useState(null)

  // Separação da fatura é lembrada por leilão + comprador (conveniência; só apresentação)
  const chaveSep = (l, c) => `fatura-comprador-sep:${l}:${c}`
  const lerSep = (l, c) => {
    try { const r = localStorage.getItem(chaveSep(l, c)); return r ? JSON.parse(r) : null } catch { return null }
  }
  const salvarSep = (l, c, cfg) => {
    try { cfg ? localStorage.setItem(chaveSep(l, c), JSON.stringify(cfg)) : localStorage.removeItem(chaveSep(l, c)) } catch { /* ignora */ }
  }

  useEffect(() => {
    if (!orgId) return
    mapasService.getConfig(orgId).then(cfg => {
      setCfgBlocos({
        medias_sexo: cfg.fatura_comprador_mostrar_medias_sexo !== false,
        media_geral: cfg.fatura_comprador_mostrar_media_geral !== false,
        gta_nota:    cfg.fatura_comprador_mostrar_gta_nota    !== false,
        ocultar_linha_gta_nota: !!cfg.fatura_comprador_ocultar_linha_gta_nota,
        ocultar_coluna_gado:    !!cfg.fatura_comprador_ocultar_coluna_gado,
        comissao_em_linha:      !!cfg.fatura_comprador_comissao_em_linha,
        ocultar_parcelas:       !!cfg.fatura_comprador_ocultar_parcelas,
        ordenar_pgtos_por_data: !!cfg.fatura_comprador_ordenar_pgtos_por_data,
        total_compra:           cfg.fatura_comprador_total_compra === 'liquido' ? 'liquido' : 'bruto',
      })
      setDbVendedor(!!cfg.mapa_comprador_dados_bancarios_vendedor)
      if (cfg.fatura_comprador_colunas_ativas && cfg.fatura_comprador_colunas_ativas.length > 0) {
        setColunasAtivas(cfg.fatura_comprador_colunas_ativas)
      }
    }).catch(() => {})
    setLoadingLeil(true)
    leiloesService.getByOrganizador(orgId, { status: 'on' })
      .then(list => {
        const sorted = [...list].sort((a, b) => new Date(b.data_leilao) - new Date(a.data_leilao))
        setLeiloes(sorted)
        if (sorted.length > 0) setLeilaoId(String((sorted.find(l => l.status === 'on') || sorted[0]).id))
      })
      .finally(() => setLoadingLeil(false))
  }, [orgId])

  useEffect(() => {
    if (!orgId || !leilaoId) { setCompradores([]); return }
    setLoadingComp(true)
    setDados(null)
    mapasService.getCompradores(orgId, leilaoId, { tipo: tipo === 'vendas_defesa' ? 'compras_ou_defesas' : 'compras' })
      .then(list => { setCompradores(list); setCompradorId('') })
      .catch(() => setCompradores([]))
      .finally(() => setLoadingComp(false))
  }, [orgId, leilaoId, tipo])

  const fetchDados = useCallback(async (cId, t, sep = null) => {
    if (!orgId || !leilaoId) return
    setLoadingPrev(true)
    setDados(null)
    setErro(null)
    try {
      const result = await mapasService.getFaturaCompradorDados(orgId, leilaoId, {
        compradorId: cId || undefined,
        tipo: t,
        separacao: cId ? sep : null,
      })
      setDados(result)
    } catch (err) {
      setErro(err.response?.data?.detail || err.message || 'Erro ao carregar dados')
    } finally {
      setLoadingPrev(false)
    }
  }, [orgId, leilaoId])

  useEffect(() => {
    if (!orgId || !leilaoId || loadingComp) return
    const sep = compradorId ? lerSep(leilaoId, compradorId) : null
    setSeparacao(sep)
    fetchDados(compradorId, tipo, sep)
  }, [compradorId, tipo, leilaoId, orgId, loadingComp]) // eslint-disable-line

  if (!orgId) return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">
      Selecione um organizador para gerar a Fatura do Comprador.
    </div>
  )

  return (
    <div className="space-y-4">
      {/* Título */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <FileText className="w-5 h-5 text-cyan-600" />
        <h1 className="text-lg font-semibold text-gray-800">Fatura do Comprador</h1>
        {loadingPrev && <RefreshCw className="w-4 h-4 animate-spin text-blue-500 ml-1" />}
      </div>

      {/* Filtros — linha única compacta */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-3">

          {/* Leilão */}
          <div className="flex-[3] min-w-[200px]">
            <Select label="Leilão" value={leilaoId}
              onChange={v => { setLeilaoId(v); setDados(null) }}
              disabled={loadingLeil}>
              {loadingLeil
                ? <option>Carregando...</option>
                : leiloes.map(l => <option key={l.id} value={String(l.id)}>{l.nome}</option>)
              }
            </Select>
          </div>

          {/* Comprador */}
          <div className="flex-[3] min-w-[200px]">
            <Select label="Comprador" value={compradorId}
              onChange={v => { setCompradorId(v); setDados(null) }}
              disabled={loadingComp || !leilaoId}>
              <option value="">TODOS OS COMPRADORES</option>
              {[...compradores]
                .sort((a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR', { sensitivity: 'base' }))
                .map(c => (
                  <option key={c.id} value={String(c.id)}>{c.nome}</option>
                ))}
            </Select>
          </div>

          {/* Tipo */}
          <div className="flex-[1.5] min-w-[130px]">
            <Select label="Tipo" value={tipo}
              onChange={v => { setTipo(v); setDados(null) }}>
              <option value="padrao">SÓ COMPRAS</option>
              <option value="vendas_defesa">COMPRAS E DEFESAS</option>
            </Select>
          </div>

          {/* Toggle dados bancários */}
          <div className="flex flex-col gap-1">
            <span className="text-xs text-gray-500 font-medium">Dados banc. vendedor</span>
            <div className="flex items-center h-[38px] px-3 bg-gray-50 rounded-lg border border-gray-200">
              <Toggle checked={dbVendedor} onChange={setDbVendedor}
                label={dbVendedor ? 'Sim' : 'Não'} />
            </div>
          </div>

        </div>
      </div>

      {/* Erro */}
      {erro && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
          {erro}
        </div>
      )}

      {/* Preview com scroll horizontal no mobile */}
      {dados && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <FaturaCompradorPreview
              dados={dados}
              orgId={orgId}
              leilaoId={leilaoId}
              compradorId={compradorId}
              tipo={tipo}
              showDbVendedor={dbVendedor}
              showMediasSexo={cfgBlocos.medias_sexo}
              showMediaGeral={cfgBlocos.media_geral}
              showGtaNota={cfgBlocos.gta_nota}
              ocultarLinhaGtaNota={cfgBlocos.ocultar_linha_gta_nota}
              ocultarColunaGado={cfgBlocos.ocultar_coluna_gado}
              comissaoEmLinha={cfgBlocos.comissao_em_linha}
              ocultarParcelas={cfgBlocos.ocultar_parcelas}
              colunasAtivas={colunasAtivas}
              ordenarPgtosPorData={cfgBlocos.ordenar_pgtos_por_data}
              totalCompraModo={cfgBlocos.total_compra}
              separacao={separacao}
              onSeparar={(cfg) => { setSeparacao(cfg); salvarSep(leilaoId, compradorId, cfg); fetchDados(compradorId, tipo, cfg) }}
              onReload={() => fetchDados(compradorId, tipo, separacao)}
            />
          </div>
        </div>
      )}

      {!dados && !loadingPrev && !erro && leilaoId && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 text-center text-gray-400 text-sm">
          Selecione um comprador acima para visualizar a fatura.
        </div>
      )}
    </div>
  )
}
